import { NamingPattern } from "../types/rename.types";
import { webAdbService } from "./webadb.service";

export class RenameService {
  private static instance: RenameService;

  private constructor() {}

  public static getInstance(): RenameService {
    if (!RenameService.instance) {
      RenameService.instance = new RenameService();
    }
    return RenameService.instance;
  }

  public readonly PREDEFINED_PATTERNS: NamingPattern[] = [
    {
      id: 'datetime_model_action',
      name: '日期时间_型号_动作模式',
      description: '格式: YYYYMMDD_HHMMSS_model_序号-动作-环境-状态',
      regex: /^(\d{8})_(\d{6})_([a-zA-Z0-9]+)_([^-]+)-([^-]+)-([^-]+)-(.+)$/,
      variables: [
        { name: 'date', displayName: '日期', group: 1 },
        { name: 'time', displayName: '时间', group: 2 },
        { name: 'model', displayName: '型号', group: 3 },
        { name: 'sequence', displayName: '序号', group: 4 },
        { name: 'action', displayName: '动作', group: 5 },
        { name: 'environment', displayName: '环境', group: 6 },
        { name: 'status', displayName: '状态', group: 7 },
      ],
      example: '20240905_094407_o65_bt02-Normal_Walk-outdoor-Offline'
    },
    {
      id: 'simple_date',
      name: '简单日期模式',
      description: '格式: YYYYMMDD_名称',
      regex: /^(\d{8})_(.+)$/,
      variables: [
        { name: 'date', displayName: '日期', group: 1 },
        { name: 'name', displayName: '原名', group: 2 },
      ],
      example: '20240119_test'
    }
  ];

  extractVariables(fileName: string, pattern: NamingPattern): Record<string, string> {
    const match = fileName.match(pattern.regex);
    if (!match) return {};

    const extracted: Record<string, string> = {};
    pattern.variables.forEach(v => {
      extracted[v.name] = match[v.group] || '';
    });
    return extracted;
  }

  renderTemplate(template: string, variables: Record<string, string>): string {
    return template.replace(/\{([^}:]+)(?::([^}]+))?\}/g, (match, varName, formatter) => {
      let value = variables[varName];
      if (value === undefined) return match;

      if (formatter === 'YYYY-MM-DD' && value.length === 8) {
        return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
      }
      if (formatter === 'HH:MM:SS' && value.length === 6) {
        return `${value.slice(0, 2)}:${value.slice(2, 4)}:${value.slice(4, 6)}`;
      }
      if (formatter === 'upper') return value.toUpperCase();
      if (formatter === 'lower') return value.toLowerCase();
      if (formatter?.startsWith('pad') && !isNaN(Number(value))) {
        const len = parseInt(formatter.replace('pad', ''));
        return value.padStart(len, '0');
      }

      return value;
    });
  }

  validateFileName(name: string): { isValid: boolean; error?: string } {
    if (!name) return { isValid: false, error: '文件名不能为空' };
    const illegalChars = /[<>:"/\\|?*]/;
    if (illegalChars.test(name)) return { isValid: false, error: '包含非法字符' };
    if (name.length > 255) return { isValid: false, error: '文件名过长' };
    return { isValid: true };
  }

  applyRegexReplace(fileName: string, pattern: string, replacement: string): { result: string; error?: string } {
    try {
      const regex = new RegExp(pattern, 'g');
      return { result: fileName.replace(regex, replacement) };
    } catch (e: any) {
      return { result: fileName, error: `正则错误: ${e.message}` };
    }
  }

  validateRegex(pattern: string): { isValid: boolean; error?: string } {
    try {
      new RegExp(pattern);
      return { isValid: true };
    } catch (e: any) {
      return { isValid: false, error: e.message };
    }
  }

  async renameOnDevice(adb: any, oldPath: string, newPath: string): Promise<void> {
    await webAdbService.rename(adb, oldPath, newPath);
  }

  async renameLocal(handle: FileSystemHandle, targetDirHandle: FileSystemDirectoryHandle, newName: string): Promise<void> {
    if ('move' in handle && typeof (handle as any).move === 'function') {
      await (handle as any).move(targetDirHandle, newName);
    } else {
      // Fallback for browsers without .move()
      if (handle.kind === 'file') {
        const fileHandle = handle as FileSystemFileHandle;
        const newFileHandle = await targetDirHandle.getFileHandle(newName, { create: true });
        const file = await fileHandle.getFile();
        const writable = await newFileHandle.createWritable();
        await writable.write(file);
        await writable.close();
        await targetDirHandle.removeEntry(handle.name);
      } else {
        // Directory: create new, copy contents, delete old
        const oldDirHandle = handle as FileSystemDirectoryHandle;
        const newDirHandle = await targetDirHandle.getDirectoryHandle(newName, { create: true });
        await this.copyDirContents(oldDirHandle, newDirHandle);
        await targetDirHandle.removeEntry(handle.name, { recursive: true });
      }
    }
  }

  private async copyDirContents(src: FileSystemDirectoryHandle, dest: FileSystemDirectoryHandle): Promise<void> {
    const entries: FileSystemHandle[] = [];
    for await (const entry of src.values()) {
      entries.push(entry);
    }

    const CONCURRENCY = navigator.hardwareConcurrency || 4;
    const active = new Set<Promise<void>>();

    for (const entry of entries) {
      const task = (async () => {
        if (entry.kind === 'file') {
          const file = await (entry as FileSystemFileHandle).getFile();
          const newFile = await dest.getFileHandle(entry.name, { create: true });
          const writable = await newFile.createWritable();
          await writable.write(file);
          await writable.close();
        } else {
          const newSubDir = await dest.getDirectoryHandle(entry.name, { create: true });
          await this.copyDirContents(entry as FileSystemDirectoryHandle, newSubDir);
        }
      })().finally(() => active.delete(task));

      active.add(task);
      if (active.size >= CONCURRENCY) {
        await Promise.race(active);
      }
    }
    await Promise.all(active);
  }

  // Local storage for templates
  getSavedTemplates(): any[] {
    const saved = localStorage.getItem('webadb_saved_templates');
    return saved ? JSON.parse(saved) : [];
  }

  saveTemplate(name: string, template: string) {
    const templates = this.getSavedTemplates();
    templates.push({ id: crypto.randomUUID(), name, template, createdAt: Date.now() });
    localStorage.setItem('webadb_saved_templates', JSON.stringify(templates));
  }
}

export const renameService = RenameService.getInstance();
